import { artifactExpandedTitle } from "@/shared/artifactTitle.ts";
import { OkThxWordmark, OkthxLogo } from "@/components/icons/Logo.tsx";
import { AppletPreview } from "@/components/artifacts/AppletPreview.tsx";
import { ArtifactErrorBoundary } from "@/components/artifacts/ArtifactErrorBoundary.tsx";
import { ArtifactView } from "@/components/artifacts/view/ArtifactView.tsx";
import {
  MainContentWrapper,
  SiteHeaderContent,
  ViewWrapper,
} from "@/components/layout/ViewWrapper.tsx";
import { PublicLogoNav } from "@/components/layout/PublicLogoNav.tsx";
import { Loader } from "@/components/loader/Loader.tsx";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog.tsx";
import { Card } from "@/components/ui/card.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import { artifactQueries } from "@/hooks/artifactQueries.ts";
import { artifactLibraryQueries } from "@/hooks/useArtifactLibrary.ts";
import { api, apiErrorMessage } from "@/lib/http.ts";
import { resolveApiPath } from "@/lib/utils.ts";
import { getErrorStatus } from "@/shared/retryPolicy.ts";
import { APPLET_WRITER_PARAM } from "@/types/appletMessages.ts";
import type { ClaimSharedArtifactReq, SharedArtifactPayload } from "@/types/sharedArtifact.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "wouter";

/**
 * A single artifact, addressed by share token.
 *
 * Public: rendered identically whether or not the visitor has a session. Signing
 * in changes exactly one thing — a viewer who could save the artifact to their
 * library is asked whether to. The server decides that and reports it on the
 * card, so this page still makes no authenticated read of its own.
 *
 * No app chrome and no actions footer: a share link is a way to read one thing,
 * not a way into the app.
 */
export const SharedArtifact = () => {
  const { shareToken } = useParams<{ shareToken: string }>();
  const { data, error, isLoading } = useQuery(artifactQueries.bySharedToken(shareToken));

  // `NavComponent` defaults to the authenticated `LogoNav`, whose
  // `useContactProfilesEnabled` 401s without a session — and the global
  // interceptor turns that into a redirect to `/auth`. The artifact itself
  // loads fine; the visitor just never sees it.
  const headerContent = (
    <SiteHeaderContent
      title={data ? artifactExpandedTitle(data.artifact) : "Shared"}
      NavComponent={PublicLogoNav}
    />
  );

  if (isLoading) {
    return (
      <ViewWrapper headerContent={headerContent}>
        <MainContentWrapper columns={7} className="py-4">
          <Loader className="py-8" />
        </MainContentWrapper>
      </ViewWrapper>
    );
  }

  if (error || !data) {
    // This is the only surface that ever sees a 410 — the token endpoint is the
    // only thing that returns one — so the "turned off" copy lives here rather
    // than in a shared error helper.
    const message =
      getErrorStatus(error) === 410
        ? "This share link was turned off by its owner."
        : "This link isn't available.";
    return (
      <ViewWrapper headerContent={headerContent}>
        <MainContentWrapper columns={7} className="py-4">
          <Card className="p-6 text-muted-foreground text-sm">{message}</Card>
        </MainContentWrapper>
      </ViewWrapper>
    );
  }

  const { artifact } = data;

  const saveDialog = data.claimable === true && (
    <SaveToLibraryDialog
      // Keyed so a "Not now" on one link never silences the next.
      key={data.shareToken}
      shareToken={data.shareToken}
      title={artifactExpandedTitle(artifact)}
    />
  );

  if (artifact.artifactType === "applet") {
    return (
      <>
        <FullScreenApplet artifact={artifact} shareToken={data.shareToken} />
        {saveDialog}
      </>
    );
  }

  return (
    <ViewWrapper headerContent={headerContent}>
      <MainContentWrapper columns={7} className="py-4">
        <Card className="overflow-hidden">
          {artifact.view ? (
            <ArtifactErrorBoundary key={artifact.id} artifact={artifact}>
              <ArtifactView
                view={artifact.view}
                // The shared payload carries no thread or message — it is one
                // artifact, not a conversation. These sentinels never reach a
                // request: `isReadOnly` disables every patch path, and the
                // applet frame takes its src from `appletPreviewSrc` instead.
                threadId={-1}
                messageId={-1}
                artifactId={artifact.id}
                isReadOnly
                isSharedThread
                appletPreviewSrc={resolveApiPath(
                  `/api/p/shared/a/${data.shareToken}/appletPreview`,
                )}
                maximized
                patcherOverride={READ_ONLY_PATCHER}
              />
            </ArtifactErrorBoundary>
          ) : (
            <p className="p-4 text-muted-foreground text-sm">This item can&apos;t be displayed.</p>
          )}
        </Card>
        <div className="flex justify-center pt-6">
          <a href="/" aria-label="Okthx home">
            <OkThxWordmark />
          </a>
        </div>
      </MainContentWrapper>
      {saveDialog}
    </ViewWrapper>
  );
};

/**
 * This browser's key for one link's applet, so the applet can tell which of its
 * records this anonymous visitor wrote.
 *
 * Per link, so visits to different links can't be tied together, and kept
 * across visits so a reload still knows. Null when this browser can't make or
 * keep one: the key marks records as the visitor's own for display and
 * authorizes nothing, so going without costs only that.
 */
function appletWriterKey(shareToken: string): string | null {
  const storageKey = `okthx:appletWriter:${shareToken}`;
  try {
    const existing = localStorage.getItem(storageKey);
    if (existing) return existing;
    const key = crypto.randomUUID();
    localStorage.setItem(storageKey, key);
    return key;
  } catch {
    return null;
  }
}

/**
 * A shared applet, alone on the page.
 *
 * An applet is a whole app, so it gets the whole screen: no header, card, or
 * wordmark eating into a phone's viewport. The branding moves to places that
 * never cover the applet's own UI — the loading screen and the tab title. The
 * frame is sandboxed and opaque to us, so we can't know where its controls sit,
 * which rules out anything overlaid once it has loaded.
 */
const FullScreenApplet = ({
  artifact,
  shareToken,
}: {
  artifact: Extract<SharedArtifactPayload, { artifactType: "applet" }>;
  shareToken: string;
}) => {
  const title = artifactExpandedTitle(artifact);
  const writerKey = useMemo(() => appletWriterKey(shareToken), [shareToken]);
  const writerQuery =
    writerKey === null ? "" : `?${APPLET_WRITER_PARAM}=${encodeURIComponent(writerKey)}`;

  useEffect(() => {
    const previous = document.title;
    document.title = `${title} · Okthx`;
    return () => {
      document.title = previous;
    };
  }, [title]);

  // Applets are always light on white, unlike the app's warm off-white. Match
  // the browser's toolbar tint to the applet, and stop iOS bouncing this page
  // (and showing its edge) when a scroll inside the frame hits the end.
  useEffect(() => {
    const themeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const previousTheme = themeColor?.content;
    const root = document.documentElement;
    const previousOverscroll = root.style.overscrollBehavior;
    themeColor?.setAttribute("content", "#ffffff");
    root.style.overscrollBehavior = "none";
    return () => {
      if (themeColor && previousTheme !== undefined) themeColor.content = previousTheme;
      root.style.overscrollBehavior = previousOverscroll;
    };
  }, []);

  return (
    // The app sets `viewport-fit=cover`, so without the insets an edge-to-edge
    // applet would sit under the notch and the home indicator.
    <div className="h-dvh w-full bg-white pt-[env(safe-area-inset-top)] pr-[env(safe-area-inset-right)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)]">
      <ArtifactErrorBoundary key={artifact.id} artifact={artifact}>
        <AppletPreview
          // Sentinels: `sharedPreviewSrc` keeps them out of every request.
          threadId={-1}
          messageId={-1}
          artifactId={artifact.id}
          title={artifact.title}
          canRequestFix={false}
          sharedPreviewSrc={resolveApiPath(
            `/api/p/shared/a/${shareToken}/appletPreview${writerQuery}`,
          )}
          fill
          loadingFallback={
            <div className="flex flex-col items-center gap-3 text-muted-foreground">
              <OkthxLogo className="size-12 animate-pulse text-foreground" />
              <span className="text-sm">Made with Okthx</span>
            </div>
          }
        />
      </ArtifactErrorBoundary>
    </div>
  );
};

/**
 * Saves a share link's artifact to the signed-in viewer's library.
 *
 * Success means the server confirmed the viewer now holds access, so a link
 * turned off mid-save surfaces as its 410 rather than a false "Saved".
 */
const useSaveToLibrary = (shareToken: string, { onSaved }: { onSaved: () => void }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await api.post<null, ClaimSharedArtifactReq>("/api/a/artifacts/claim", { shareToken });
    },
    onSuccess: () => {
      onSaved();
      void queryClient.invalidateQueries({ queryKey: artifactLibraryQueries.all() });
    },
    onError: (error) =>
      errorToast(
        getErrorStatus(error) === 410
          ? "This share link was turned off by its owner."
          : (apiErrorMessage(error) ?? "Couldn't save this."),
      ),
  });
};

/**
 * Asks a signed-in viewer whether to keep what they were sent.
 *
 * Opening a link writes nothing to the viewer's account — anyone can send one —
 * so saving it is this explicit choice. Declining leaves the card readable.
 */
const SaveToLibraryDialog = ({ shareToken, title }: { shareToken: string; title: string }) => {
  const [open, setOpen] = useState(true);
  const save = useSaveToLibrary(shareToken, { onSaved: () => setOpen(false) });

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!save.isPending) setOpen(next);
      }}
    >
      <AlertDialogContent
        onEscapeKeyDown={(e) => {
          if (save.isPending) e.preventDefault();
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>Save to your library?</AlertDialogTitle>
          <AlertDialogDescription>
            Someone shared “{title}” with you. Saving it adds it to your library, where your
            assistant can find it too.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            disabled={save.isPending}
            data-statsig-name="shared-artifact-save-decline"
          >
            Not now
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              // Radix closes on action-select; saving can fail, so the close
              // waits for `onSuccess`.
              e.preventDefault();
              save.mutate();
            }}
            disabled={save.isPending}
            data-statsig-name="shared-artifact-save-accept"
          >
            {save.isPending ? "Saving…" : "Save"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

/**
 * Belt to `isReadOnly`'s braces: the default patcher POSTs to a thread-scoped
 * route, which a shared view has no thread for. Overriding it means a block that
 * ignores `isReadOnly` still cannot issue a write.
 */
const READ_ONLY_PATCHER = {
  patch: async () => undefined,
  patchOne: async () => undefined,
  isPending: false,
};
